/** What the three scope tables share, as pure functions - ADR-0212 decision 2.
 *
 * Nothing here fetches and **nothing here sums**: a total is the server's (`GET /inventory`), and a
 * count of rows is a count. The table filters the figures it was given and says how many it kept
 * out of how many the server counted.
 */

import type { Figure, FigureCut, FigureFacets, FigureSortColumn, Inventory } from "../../api/queries";
import type { components } from "../../api/schema";
import type { FilterDef, FilterState } from "../../components/ui";
import { originWord } from "../../components/ui";
import { CATEGORY_LABEL } from "../../lib/categoryLabel";
import { isBelowGradeA } from "../../lib/estimate";
import { monthLabel, shortDate } from "../../lib/format";

export type Scope = "1" | "2" | "3";

type EngineCategory = components["schemas"]["Category"];

/** The scope each engine category belongs to. **A `Record` over the generated union**, so a category
 * the server adds fails the typecheck here instead of landing in no table. This mirrors the engine's
 * own sets (`engine/types.py`: `SCOPE_1_CATEGORIES`, `SCOPE_2_CATEGORIES`; transmission loss is
 * Scope 3 category 3 and is in neither), and the figures themselves carry their scope, so this is
 * only used to place a *source* in the census, which names a category and no scope. */
export const SCOPE_OF: Readonly<Record<EngineCategory, Scope>> = {
  stationary: "1",
  mobile: "1",
  fugitive: "1",
  process: "1",
  electricity: "2",
  steam: "2",
  heat: "2",
  cooling: "2",
  transmission_loss: "3",
};

/** The scope of a census category, or `undefined` for one this table has never heard of. */
export function scopeOfCategory(category: string): Scope | undefined {
  return (SCOPE_OF as Readonly<Record<string, Scope | undefined>>)[category];
}

/** A category or carrier as words: underscores read as spaces and nothing else is guessed. */
export function words(value: string): string {
  return value.replace(/_/g, " ");
}

/** An accounting category as a customer says it: `stationary` is "Fuel burned on site" (the one table in
 * `lib/categoryLabel`, which the Sources form and the factor library already read), and a category the
 * table does not know is spoken by `words`, never dropped (review items 11 and 12). */
export function categoryWords(category: string): string {
  return CATEGORY_LABEL[category] ?? words(category);
}

// ------------------------------------------------------------------------------------- status

/** Where a figure stands, as a word. **Derived, and every state is a different fact:**
 *
 * - `verified`: a person accepted it and its evidence chain holds.
 * - `awaiting`: a hand-entered figure nobody has accepted. It already counts in the totals and
 *   arrives unverified; a different Admin or Approver accepts it (ADR-0067).
 * - `not_verified`: no verifier is recorded and it did not come in by hand.
 * - `gap`: a verifier is recorded but the evidence chain breaks somewhere (`evidence_broken_at`).
 *
 * The order of the tests is the order of the remedies: an unaccepted figure is waiting on a person
 * before its evidence is the question.
 */
export type FigureStatus = "verified" | "awaiting" | "not_verified" | "gap";

export const STATUS_WORD: Readonly<Record<FigureStatus, string>> = {
  verified: "Verified",
  awaiting: "Awaiting accept",
  not_verified: "Not verified",
  gap: "Evidence gap",
};

export const STATUS_ORDER: readonly FigureStatus[] = ["verified", "awaiting", "not_verified", "gap"];

export function statusOf(figure: Pick<Figure, "verification_id" | "receipt_is_complete" | "origin">): FigureStatus {
  if (!figure.verification_id) return figure.origin === "manual" ? "awaiting" : "not_verified";
  return figure.receipt_is_complete ? "verified" : "gap";
}

/** A figure below grade A. The server's grade, read through the one estimate reading (`lib/estimate`). */
export function isEstimate(figure: Pick<Figure, "quality">): boolean {
  return isBelowGradeA(figure.quality);
}

// ------------------------------------------------------------------------------------- period

/** "Jan 2025" for a whole calendar month and "1 Jan 2025 - 31 Mar 2025" otherwise. Written from the
 * ISO strings' own parts, so no time zone is involved. */
export function periodWords(start: string, end: string): string {
  const sameMonth = start.slice(0, 7) === end.slice(0, 7);
  if (sameMonth && start.endsWith("-01")) {
    const last = new Date(Date.UTC(Number(end.slice(0, 4)), Number(end.slice(5, 7)), 0)).getUTCDate();
    if (Number(end.slice(8, 10)) === last) return monthLabel(start.slice(0, 7));
  }
  return `${shortDate(start)} – ${shortDate(end)}`;
}

// ------------------------------------------------------------------------------------- filters

export const FILTER_IDS = ["site", "category", "carrier", "origin", "status", "period"] as const;

/** How a figure arrived, in the order a table offers it. The server's closed set. */
export const ORIGINS = ["uploaded", "manual", "imported", "connector"] as const;

/** The filter chips, **offered from the server's facets** (`GET /figures/facets`: the values present in
 * the window and scope) and never from the figures a page happens to hold, and never from a taxonomy:
 * a list of every category the engine knows would offer a reader choices that return nothing.
 *
 * Source and Status are the exceptions, and for a reason: both are derived on read, so no `DISTINCT`
 * can list them. They are offered whole - a choice that returns nothing says "No figure matches these
 * filters", which is the true answer, where a chip that vanished would say nothing at all. */
export function facetFilterDefs(
  facets: FigureFacets | undefined,
  options: {
    carrierLabel: string;
    withCategory: boolean;
    /** Scope 2 with the market column on: the instrument tiers present. */
    withInstrument: boolean;
  },
): FilterDef[] {
  const defs: FilterDef[] = [
    {
      id: "site",
      label: "Site",
      options: (facets?.sites ?? []).map((site) => ({ value: site.id, label: site.name })),
    },
  ];
  if (options.withCategory) {
    defs.push({
      id: "category",
      label: "Category",
      options: (facets?.categories ?? []).map((value) => ({ value, label: words(value) })),
    });
  }
  defs.push(
    {
      id: "carrier",
      label: options.carrierLabel,
      options: (facets?.carriers ?? []).map((value) => ({ value, label: words(value) })),
    },
    { id: "origin", label: "Source", options: ORIGINS.map((value) => ({ value, label: originWord(value) })) },
    { id: "status", label: "Status", options: STATUS_ORDER.map((value) => ({ value, label: STATUS_WORD[value] })) },
    {
      id: "period",
      label: "Period",
      options: (facets?.months ?? []).map((value) => ({ value, label: monthLabel(value) })),
    },
  );
  if (options.withInstrument) {
    defs.push({
      id: "instrument",
      label: "Instrument",
      options: (facets?.instruments ?? []).map((value) => ({ value, label: TIER_WORD[value] ?? words(value) })),
    });
  }
  // A chip with nothing to choose between is noise - except where the facets have not arrived yet,
  // when the chips still stand so the filter line does not jump.
  return defs.filter((def) => def.options.length > 0 || facets === undefined);
}

/** What a filter state asks the server for. The filter ids are the table's; the server's names are
 * `FigureCut`'s. A value that names nothing present is the server's to answer with an empty page. */
export function cutOfFilters(
  state: FilterState,
  extra: {
    scope: Scope;
    sorts: readonly { columnId: string; direction: "asc" | "desc" }[];
    includeMarket: boolean;
  },
): FigureCut {
  const picked = (id: string) => state.values[id] ?? [];
  const sorts = extra.sorts.flatMap((level) =>
    isSortColumn(level.columnId) ? [{ column: level.columnId, direction: level.direction }] : [],
  );
  return {
    scope: extra.scope,
    ...(picked("site").length ? { facilityIds: picked("site") } : {}),
    ...(picked("category").length ? { categories: picked("category") } : {}),
    ...(picked("carrier").length ? { carriers: picked("carrier") } : {}),
    ...(picked("origin").length ? { origins: picked("origin") } : {}),
    ...(picked("status").length ? { statuses: picked("status") } : {}),
    ...(picked("period").length ? { months: picked("period") } : {}),
    ...(picked("instrument").length ? { instruments: picked("instrument") } : {}),
    ...(state.search.trim() ? { search: state.search.trim() } : {}),
    ...(sorts.length ? { sorts } : {}),
    ...(extra.includeMarket ? { includeMarket: true } : {}),
  };
}

/** The columns of the figures tables that the server orders by (`FigureSortColumn`). */
const SORT_COLUMNS: ReadonlySet<string> = new Set([
  "tonnes",
  "quantity",
  "period",
  "site",
  "carrier",
  "category",
  "grade",
]);
function isSortColumn(id: string): id is FigureSortColumn {
  return SORT_COLUMNS.has(id);
}

/** How many figures sit at each grade in a window, **as the server counted them** (`by_category`'s
 * `grade_counts`, summed over the categories of a scope, or of every scope when none is named). A count of
 * rows from the totals read, never from a list of figures: it is the whole window however large it is.
 * `undefined` while the totals are not read - never zeros, which would be a window with nothing in it. */
export function gradeCountsOf(
  inventory: Inventory | undefined,
  scope?: Scope,
): Record<"A" | "B" | "C" | "D", number> | undefined {
  // A response that does not state its categories (an older server) states no grades: that is unknown,
  // which is not the same as a window with none.
  if (!inventory || !Array.isArray(inventory.by_category)) return undefined;
  const out = { A: 0, B: 0, C: 0, D: 0 };
  for (const cell of inventory.by_category) {
    if (scope && cell.scope !== scope) continue;
    // A cell without its grades (an older server) makes the whole answer unknown, never a partial one.
    if (!cell.grade_counts) return undefined;
    for (const grade of ["A", "B", "C", "D"] as const) out[grade] += cell.grade_counts[grade] ?? 0;
  }
  return out;
}

/** The market-based instrument tiers, in words. The key is the server's `market_tier`. */
export const TIER_WORD: Readonly<Record<string, string>> = {
  certificate: "Certificate",
  contract: "Contract",
  supplier_specific: "Supplier factor",
  residual_mix: "Residual mix",
  location_fallback: "Location factor",
};
